import React from 'react';
import {
    Progress, Button,
} from 'reactstrap';
import moment from 'moment';
import { faDownload, faRefresh, faToolbox } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { httpClient } from '@s20/services/httpClient';
import {
    createDataSource,
    createGridOptions,
    DataGrid,
    indexCellRenderer,
    EditLinkCellRenderer,
    TextFilterDefaults,
} from '../common/dataGrid/DataGrid';
import DataGridSelectFilter from '../common/dataGrid/DataGridSelectFilter';
import CommonContext, { ApiRoutes } from '../Common';
import {
    PageHeading, PageWrap, onFieldChange, toasty, Input,
    SmallButton,
} from '../common/forms/FormElements';
import DataGridToolbar from '../common/dataGrid/DataGridToolbar';
import DataGridSelectFloatingFilter from '../common/dataGrid/DataGridSelectFloatingFilter';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import { Modal } from '../common/Modal';
import { Can } from '../Can';
import { EquipmentForm } from './EquipmentForm';

const onExportClick = async ({ selectedWeekOf, selectedDispatchLocation }) => {
    if (!selectedWeekOf || selectedDispatchLocation === null || selectedDispatchLocation === undefined) return;

    const model = {
        weekOf: new Date(selectedWeekOf),
        dispatchLocationId: selectedDispatchLocation,
    };

    try {
        const fileName = `TrucksReportExport${moment().format(
            'YYYY-M-D_hhmmss',
        )}.xlsx`;

        await httpClient.downloadFile(
            ApiRoutes.equipment.trucksReport(),
            model,
            fileName,
        );
    } catch (error) {
        toasty.error('There was a problem getting the weekly export.');
        console.error({ error });
    }
};

class EquipmentIndex extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);

        const params = new URLSearchParams(props.location.search);

        this.state = {
            loading: true,
            roles: [],
            dispatchLocations: [],
            showEquipmentForm: params.has('new') || params.has('eId'),
            selectedEquipmentId: params.has('eId') ? params.get('eId') : null,
        };

        this.onAddEquipment = this.onAddEquipment.bind(this);
        this.onEditEquipment = this.onEditEquipment.bind(this);
        this.onEquipmentFormClosed = this.onEquipmentFormClosed.bind(this);
        this.refreshNetSuiteAssets = this.refreshNetSuiteAssets.bind(this);
        this.onChange = this.onChange.bind(this);
    }

    componentDidMount() {
        return this.populateState();
    }

    componentDidUpdate(prevProps) {
        const prevSearch = prevProps.location?.search;
        const nextSearch = this.props.location?.search;
        if (prevSearch !== nextSearch) {
            const params = new URLSearchParams(nextSearch);
            const nextId = params.has('eId') ? params.get('eId') : null;
            if (String(nextId) !== String(this.state.selectedEquipmentId)) {
                this.context.setFormOpened(!!nextId);
                this.setState({
                    selectedEquipmentId: nextId,
                    showEquipmentForm: !!nextId || params.has('new'),
                });
            }
        }
    }

    onAddEquipment = () => {
        this.context.setFormOpened(true);
        this.setState({
            selectedEquipmentId: null,
            showEquipmentForm: true,
        });
    };

    onEditEquipment = (id) => {
        this.context.setFormOpened(true);
        this.setState({
            selectedEquipmentId: id,
            showEquipmentForm: true,
        });
    };

    onEquipmentFormClosed = () => {
        // doesnt fire the form closed hanlder in datagridtoolbar for some reason
        this.context.setFormOpened(false);
        this.setState({
            selectedEquipmentId: null,
            showEquipmentForm: false,
        });
        this.state.gridOptions.refresh();
    };

    refreshNetSuiteAssets = async () => {
        this.setState({
            loadingData: true,
        });
        const response = await httpClient.post(
            ApiRoutes.equipment.refreshNetSuiteAssets(),
            null,
            httpClient.format.none,
        );

        if (response.ok) {
             toasty.success('NetSuite Equipment refresh completed successfully.');
            this.state.gridOptions.refresh();
        } else {
            toasty.error('Failed to refresh NetSuite Equipment.');
        }

        this.setState({
            loadingData: false,
        });
    };

    onChange = onFieldChange;

    async populateState() {
        const [types, statuses, dispatchLocations] = await Promise.all([
            httpClient.js(ApiRoutes.typeAheads.equipmentTypes()),
            httpClient.js(ApiRoutes.typeAheads.equipmentTypeStatuses()),
            httpClient.js(ApiRoutes.typeAheads.dispatchLocations()),
        ]);

        const gridOptions = createGridOptions(this);

        gridOptions.components = {
            selectFilter: DataGridSelectFilter,
            selectFloatingFilter: DataGridSelectFloatingFilter,
            nameRenderer: EditLinkCellRenderer,
        };

        const dispatchFilterParams = {
            suppressFilterButton: true,
            labelText: 'Filter by Dispatch',
            options: dispatchLocations,
            optionsLabel: 'label',
            optionsValue: 'value',
        };

        gridOptions.columnDefs = [
            {
                flex: 0,
                maxWidth: 80,
                headerName: '',
                valueGetter: 'node.id',
                sortable: false,
                cellRenderer: indexCellRenderer,
            },
            {
                colId: 'Description',
                sortable: true,
                headerName: 'Description',
                field: 'description',
                sort: { direction: 'asc', priority: 0 },
                cellRenderer: 'nameRenderer',
                cellRendererParams: {
                    clicked: this.onEditEquipment,
                    nameField: 'description',
                    idField: 'id',
                    title: 'View this Equipment',
                    entity: 'equipment',
                },
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'Identifier',
                sortable: true,
                headerName: 'Number',
                field: 'number',
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'EquipmentTypeId',
                sortable: true,
                headerName: 'Type',
                field: 'type',
                filter: 'selectFilter',
                filterParams: {
                    suppressMenu: true,
                    floatingFilter: true,
                    labelText: 'Filter by type',
                    options: types,
                    optionsLabel: 'label',
                    optionsValue: 'value',
                },
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                    options: types,
                    optionsLabel: 'label',
                    optionsValue: 'value',
                },
            },
            {
                colId: 'EquipmentStatusId',
                sortable: true,
                headerName: 'Status',
                field: 'status',
                filter: 'selectFilter',
                filterParams: {
                    suppressMenu: true,
                    floatingFilter: true,
                    labelText: 'Filter by type',
                    options: statuses,
                    optionsLabel: 'label',
                    optionsValue: 'value',
                },
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                    options: statuses,
                    optionsLabel: 'label',
                    optionsValue: 'value',
                    initialFilterValue: 1,
                },
            },
            {
                colId: 'DispatchLocation.CompanyName',
                headerName: 'Dispatching',
                sortable: true,
                field: 'dispatchLocation',
                tooltipField: 'dispatchLocation',
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: dispatchFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: dispatchFilterParams,
            },
            {
                colId: 'AssignedTo',
                sortable: true,
                headerName: 'Assigned To',
                field: 'assignedTo',
                sort: { direction: 'asc', priority: 0 },
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
        ];

        const dataSource = createDataSource(
            ApiRoutes.equipment.search(),
            gridOptions,
        );

        this.setState({
            loading: false,
            gridOptions,
            dataSource,
            dispatchLocations,
        });
    }

    toggleEquipmentForm = (show) => this.setState((state) => ((state.showEquipmentForm = show), state));

    handleTruckReportButtonClick = () => {
        const { dispatchLocations } = this.state;
        const dispatchOptions = [{ value: 0, label: 'All' }, ...(dispatchLocations ?? [])];

        Modal.show({
            title: 'Trucks Report',
            SaveButtonProps: {
                label: 'Export',
                savingLabel: 'Exporting',
                icon: faDownload,
            },
            onSubmit: onExportClick,
            body: () => (
                <>
                    <ValidatedSelect
                        placeholder="Select Dispatching"
                        id="selectedDispatchLocation"
                        name="selectedDispatchLocation"
                        label="Dispatching"
                        isClearable={false}
                        options={dispatchOptions}
                        required
                        error="A dispatch office must be selected"
                    />

                    <Input
                        label="Week Of"
                        id="weekOf"
                        required
                        name="selectedWeekOf"
                        type="date"
                        error="You must select a week"
                    />
                </>
            ),
        });
    };

    render() {
        const {
            loading,
            rowData,
            gridOptions,
            saving,
            loadingData,
        } = this.state;

        const isAdmin = this.context?.user?.isAdmin;

        if (loading) {
            return <Progress />;
        }

        return (
            <PageWrap>
                <PageHeading>
                    <FontAwesomeIcon
                        icon={faToolbox}
                        className="mr-2 text-muted"
                    />
                    <span>Equipment</span>
                    <span style={{ float: 'right', position: 'relative', top: '-5px' }}>

                        {isAdmin && (
                            <Button
                                size="sm"
                                color="success"
                                onClick={this.handleTruckReportButtonClick}
                            >
                                Trucks Report
                            </Button>
                        )}
                    </span>
                </PageHeading>
                <DataGridToolbar
                    entity="equipment"
                    gridApi={this.state.gridApi}
                    dataSource={this.state.dataSource}
                    onAdd={this.onAddEquipment}
                    addLabel="Add Equipment"
                    hideExcelButton
                    gridOptions={this.state.gridOptions}
                    serverExport={{
                        apiPath: ApiRoutes.equipment.excelExport(),
                        filePrefix: 'EquipmentSearch',
                    }}
                    serverExportDisabled={Boolean(
                        saving || loading || loadingData,
                    )}
                >
                    <Can
                        do="edit"
                        on="equipment"
                    >
                        <SmallButton
                            type="button"
                            onClick={this.refreshNetSuiteAssets}
                            disabled={saving || loading || loadingData}
                        >
                            <FontAwesomeIcon
                                icon={faRefresh}
                                className="mr-2"
                            />
                            Refresh From NetSuite
                        </SmallButton>
                    </Can>
                </DataGridToolbar>
                <DataGrid
                    domLayout="normal"
                    rowData={rowData}
                    gridOptions={gridOptions}
                    onNameCellClicked={this.onEditEquipment}
                    gridStatus={this.state.gridStatus}
                />

                <EquipmentForm
                    show={
                        this.state
                            .showEquipmentForm /* controls form open state */
                    }
                    selectedEquipmentId={this.state.selectedEquipmentId}
                    toggleShow={
                        this
                            .toggleEquipmentForm /* getter/setter for form open state */
                    }
                    onClose={this.onEquipmentFormClosed}
                />
            </PageWrap>
        );
    }
}
export default EquipmentIndex;
